import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import Footer from '../components/Footer';
import { ArrowRight, Rss, Sparkles, BookOpen, CircleAlert } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

const PublicChangelogPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/public/changelog/${slug}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Changelog not available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="container mx-auto max-w-3xl space-y-4">
          <Skeleton className="h-10 w-64" />
          {[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" data-testid="public-changelog-error">
        <CircleAlert className="h-10 w-10 text-muted-foreground mb-3" />
        <h1 className="text-2xl font-bold mb-2">Changelog unavailable</h1>
        <p className="text-muted-foreground mb-6 max-w-md">{error || "This changelog may be private or the URL is incorrect."}</p>
        <Button onClick={() => navigate('/')}>Back to VibeSprint</Button>
      </div>
    );
  }

  const { workspace, entries, count } = data;
  const rssUrl = `${API_URL}/api/public/changelog/${slug}/rss`;

  return (
    <div className="min-h-screen bg-background" data-testid={`public-changelog-${slug}`}>
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-xl"
                 style={{ backgroundColor: `${workspace.color || '#8B5CF6'}20` }}>
              {workspace.icon || '📦'}
            </div>
            <div>
              <h1 className="text-xl font-bold">{workspace.name}</h1>
              <p className="text-xs text-muted-foreground">Changelog · {count} entries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={rssUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-orange-500"
              data-testid="public-changelog-rss-link"
            >
              <Rss className="h-4 w-4" /> RSS
            </a>
            <a href="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <Sparkles className="h-3 w-3" /> Powered by VibeSprint
            </a>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-3xl">
        {entries.length === 0 ? (
          <Card className="p-10 text-center">
            <BookOpen className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">No entries yet</h2>
            <p className="text-sm text-muted-foreground">Check back soon.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {entries.map((e) => (
              <Card key={e.id} id={e.id} className="p-6" data-testid={`public-changelog-entry-${e.id}`}>
                <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                  <h2 className="text-xl font-bold flex-1">{e.title}</h2>
                  <span className="text-xs text-muted-foreground tabular-nums">{formatDate(e.shipped_at)}</span>
                </div>
                {e.description && (
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{e.description}</p>
                )}
                {e.category && (
                  <div className="mt-3">
                    <Badge variant="outline" className="capitalize text-[10px]">{e.category.replace('_', ' ')}</Badge>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      <section className="py-12 border-t bg-muted/20">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <BookOpen className="h-8 w-8 text-primary mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Want a changelog like this?</h2>
          <p className="text-muted-foreground mb-4">
            Mark a decision "shipped" in VibeSprint — and it auto-publishes here.
          </p>
          <Button onClick={() => navigate('/')} className="btn-cta" data-testid="public-changelog-cta">
            Try VibeSprint
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer variant="minimal" />
    </div>
  );
};

export default PublicChangelogPage;
